/**
 * The floating-control census on a phone (FLOW_SCREEN_SPEC.md §1.3).
 *
 * A Flow phone used to float five things at once over the home: the Record
 * button, the Karr Force pill, the compliance orb, the new-law popup and the
 * install banner, on top of rows that are themselves the door to record.
 * The rule this function keeps:
 *
 * - **On the Flow home only Karr Force floats.** The seven rows are the door;
 *   "Add Karr to your home screen" is a footer line there.
 * - **On every other Flow screen at most three**: the Home pill (left), the
 *   Record button (right, the one door) and Karr Force.
 * - **Karr Force floats on every phone screen**, and the person drags it
 *   wherever it is out of their way; it stays there (nozoom, founder 4 Oct
 *   2026: *"make karr force a floating button that can be moved here and
 *   there"*). Before that it was a footer line on the Flow home and an Ask
 *   half of the Home pill elsewhere.
 * - The compliance orb and the new-law popup are never drawn in Flow: their
 *   facts are Needs you rows (a count of one for the new rules).
 * - The header `+` hides below `sm` in Flow only (the Record button and the
 *   rows are the phone's doors); Books keeps everything exactly as it was.
 * - The Karr Coach never auto-opens over the Flow home (its first-run
 *   content is a Needs you row); deep screens keep their coach.
 *
 * One named predicate, `onFlowHome`, because Dashboard mounts AppLayout with
 * `currentRoute="/"` while the address bar says `/dashboard`: both are home.
 *
 * Pure, so `floatingControls.test.ts` pins every cell of the census.
 *
 * @since flow-1b (24 Sep 2026)
 */

/** Home is `/` (what Dashboard passes) or `/dashboard` (the address). */
export function onFlowHomeFor(currentRoute: string): boolean {
    return currentRoute === '/dashboard' || currentRoute === '/';
}

export interface ChromeInput {
    /** The Flow view (`business_owner`). */
    isFlow: boolean;
    currentRoute: string;
    /** A full-screen page: no chrome at all. */
    hideSidebar: boolean;
    /** Flow without a sidebar (a practice org in Flow keeps its sidebar). */
    flowNoSidebar: boolean;
}

export interface FloatingCensus {
    onFlowHome: boolean;
    /** The phone Record button (right). */
    recordFab: boolean;
    /** The Home pill (left). */
    homePill: boolean;
    /** Where Karr Force's phone entry lives: always the draggable pill (nozoom). */
    karrForce: 'floating_pill';
    complianceOrb: boolean;
    obligationPopup: boolean;
    /** The floating install banner (in Flow: a footer line on the home instead). */
    installBanner: boolean;
    /** Classes for the header `+`. */
    headerPlusClass: string;
    /** Whether the Karr Coach may open by itself on this screen. */
    coachAutoOpen: boolean;
    /**
     * Whether the Simple hub strip ("Also here") draws under the title. Not on
     * the Flow home: the home has five kinds of thing and a strip is none of
     * them, and its one link (My Businesses) already has its home in the org
     * pill (FLOW_SCREEN_SPEC.md §9). The spec assumed this strip never drew on
     * the home; measured at 360px on 24 Sep 2026, it did.
     */
    hubStrip: boolean;
}

export function floatingCensus(i: ChromeInput): FloatingCensus {
    const onFlowHome = i.isFlow && onFlowHomeFor(i.currentRoute);
    if (!i.isFlow) {
        return {
            onFlowHome: false,
            recordFab: false,
            homePill: false,
            karrForce: 'floating_pill',
            complianceOrb: true,
            obligationPopup: true,
            installBanner: true,
            headerPlusClass: 'flex',
            coachAutoOpen: true,
            hubStrip: true,
        };
    }
    const deep = !onFlowHome && !i.hideSidebar;
    const homePill = deep && i.flowNoSidebar;
    return {
        onFlowHome,
        recordFab: deep,
        homePill,
        karrForce: 'floating_pill',
        complianceOrb: false,
        obligationPopup: false,
        installBanner: false,
        headerPlusClass: 'hidden sm:flex',
        coachAutoOpen: !onFlowHome,
        hubStrip: !onFlowHome,
    };
}

/** How many controls float on a phone for this census (the Home pill counts once). */
export function floatingCount(c: FloatingCensus): number {
    return (c.recordFab ? 1 : 0)
        + (c.homePill ? 1 : 0)
        + (c.karrForce === 'floating_pill' ? 1 : 0)
        + (c.complianceOrb ? 1 : 0)
        + (c.installBanner ? 1 : 0);
}
